import { useState } from "react";
import { uploadMultipleFiles, deleteFile, createEmptyFolderFile, getDocumentByFilename } from "../services/uploadFilesApi.jsx";

interface Collection {
  files?: any[];
  [key: string]: any;
}

interface AlertConfig {
  open: boolean;
  type: "loading" | "success" | "error" | "confirm";
  message: string;
  title?: string;
  onConfirm?: () => Promise<void>;
}

interface FileValidationResult {
  valid: boolean;
  reason?: "nameLength" | "type" | "size";
}

export const useFileOperations = (
  collections: { [key: string]: Collection },
  setCollections: React.Dispatch<React.SetStateAction<{ [key: string]: Collection }>>,
  currentPath: string[],
  defaultCollection: string,
  textConfig: any,
  fetchUserDocuments: (prefix?: string, setAlertConfig?: (config: AlertConfig) => void, page?: number, isSearch?: boolean, searchTerm?: string) => Promise<void>
) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [newCollectionFiles, setNewCollectionFiles] = useState<File[]>([]);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingCollection, setIsCreatingCollection] = useState(false);
  const [fileNameError, setFileNameError] = useState<string>("");
  const [collectionNameError, setCollectionNameError] = useState<string>("");
  const [showInlineSuccessMessage, setShowInlineSuccessMessage] = useState(false);
  const [showFileUploadSuccessMessage, setShowFileUploadSuccessMessage] = useState(false);

  const validateFile = (file: File): FileValidationResult => {
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ];

    const maxSize = 10 * 1024 * 1024;

    if (file.name.length > 150) {
      return { valid: false, reason: "nameLength" };
    }

    if (!validTypes.includes(file.type)) {
      return { valid: false, reason: "type" };
    }

    if (file.size > maxSize) {
      return { valid: false, reason: "size" };
    }

    return { valid: true };
  };

  const processFileSelection = (
    files: File[],
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    isNewCollection: boolean = false
  ) => {
    const validFiles: File[] = [];
    const invalidTypes: File[] = [];
    const invalidSizes: File[] = [];
    const invalidNames: File[] = [];

    files.forEach((file) => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else if (validation.reason === "type") {
        invalidTypes.push(file);
      } else if (validation.reason === "size") {
        invalidSizes.push(file);
      } else if (validation.reason === "nameLength") {
        invalidNames.push(file);
      }
    });

    let message = "";

    if (invalidNames.length > 0) {
      const fileNames = invalidNames.map((f) => f.name).join(", ");
      message += `Los siguientes archivos exceden el límite de 150 caracteres: ${fileNames}.\n`;
      setFileNameError("El nombre del archivo no puede exceder los 150 caracteres.");
    } else {
      setFileNameError("");
    }

    if (invalidTypes.length > 0) {
      const fileNames = invalidTypes.map((f) => f.name).join(", ");
      message += `Los siguientes archivos no tienen un formato permitido: ${fileNames}. Solo se permiten PDF, Word y TXT.\n`;
    }

    if (invalidSizes.length > 0) {
      const fileNames = invalidSizes.map((f) => f.name).join(", ");
      message += `Los siguientes archivos exceden el límite de 10MB: ${fileNames}.\n`;
    }

    if (message) {
      alert(message);
    }

    setFiles(validFiles);
  };

  const handleFileSelection = (files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    processFileSelection(fileArray, setSelectedFiles);
  };

  const handleNewCollectionFileSelection = (files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    processFileSelection(fileArray, setNewCollectionFiles, true);
  };

  const handleUploadFiles = async (setAlertConfig: React.Dispatch<React.SetStateAction<AlertConfig>>) => {
    if (selectedFiles.length === 0) {
      setAlertConfig({
        open: true,
        type: "error",
        message: textConfig?.noFilesSelected || "No se han seleccionado archivos",
      });
      return;
    }

    const targetCollection = currentPath.length > 0 ? currentPath.join('/') : defaultCollection;
    if (!targetCollection) {
      setAlertConfig({
        open: true,
        type: "error",
        message: textConfig?.noCollectionSelected || "No se ha seleccionado una colección",
      });
      return;
    }

    setIsUploading(true);
    setAlertConfig({
      open: true,
      type: "loading",
      message: textConfig?.uploadingFiles || "Subiendo archivos...",
    });

    try {
      const result = await uploadMultipleFiles(selectedFiles, targetCollection);

      if (result.err && result.err.length > 0) {
        const errorMessages = result.err.map((e: any) => `${e.fileName}: ${e.error}`).join("\n");
        setAlertConfig({
          open: true,
          type: "error",
          message: `Error al subir algunos archivos:\n${errorMessages}`,
        });
      } else {
        setAlertConfig({
          open: true,
          type: "success",
          message: textConfig?.filesUploadedSuccessfully || "Archivos subidos exitosamente",
        });
        setShowFileUploadSuccessMessage(true);
        setTimeout(() => setShowFileUploadSuccessMessage(false), 3000);
      }

      setSelectedFiles([]);
      const prefix = currentPath.length > 0 ? currentPath.join("/") + "/" : undefined;
      await fetchUserDocuments(prefix);
    } catch (error: any) {
      setAlertConfig({
        open: true,
        type: "error",
        message: textConfig?.errorUploadingFiles || `Error al subir archivos: ${error.message}`,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteFile = async (fileName: string, setAlertConfig: React.Dispatch<React.SetStateAction<AlertConfig>>) => {

    setAlertConfig({
      open: true,
      type: "confirm",
      message: textConfig?.confirmDeleteFile || "¿Estás seguro de que quieres eliminar este archivo?",
      title: textConfig?.deleteFile || "Eliminar Archivo",
      onConfirm: async () => {
        try {
          let fileExists = false;
          try {
            const document = await getDocumentByFilename(fileName);
            fileExists = true;
          } catch (error) {
            fileExists = false;
          }

                    if (fileExists) {
            await deleteFile(fileName);
            setAlertConfig({
              open: true,
              type: "success",
              message: textConfig?.fileDeletedSuccessfully || "Archivo eliminado exitosamente",
            });
            const prefix = currentPath.length > 0 ? currentPath.join("/") + "/" : undefined;
            await fetchUserDocuments(prefix);
          } else {
            setAlertConfig({
              open: true,
              type: "error",
              message: `No se puede eliminar este archivo porque no existe en la base de datos. Archivo: ${fileName}`,
            });
            const prefix = currentPath.length > 0 ? currentPath.join("/") + "/" : undefined;
            await fetchUserDocuments(prefix);
            return;
          }
        } catch (error: any) {
          if (error.message && error.message.includes('404')) {
            setAlertConfig({
              open: true,
              type: "error",
              message: `El archivo no existe en la base de datos. Archivo: ${fileName}`,
            });
            const prefix = currentPath.length > 0 ? currentPath.join("/") + "/" : undefined;
            await fetchUserDocuments(prefix);
          } else {
            setAlertConfig({
              open: true,
              type: "error",
              message: textConfig?.errorDeletingFile || `Error al eliminar archivo: ${error.message}`,
            });
          }
        }
      },
    });
  };

  const handleCreateCollection = async (setAlertConfig: React.Dispatch<React.SetStateAction<AlertConfig>>) => {
    if (!newCollectionName.trim()) {
      setCollectionNameError("El nombre de la colección es requerido.");
      return;
    }

    if (newCollectionName.length > 50) {
      setCollectionNameError("El nombre de la colección no puede exceder los 50 caracteres.");
      return;
    }

    if (collections[newCollectionName]) {
      setCollectionNameError("Ya existe una colección con ese nombre.");
      return;
    }

    setIsCreatingCollection(true);
    setAlertConfig({
      open: true,
      type: "loading",
      message: textConfig?.creatingCollection || "Creando colección...",
    });

    try {
      setCollections((prev) => ({
        ...prev,
        [newCollectionName]: { files: [] },
      }));

      if (newCollectionFiles.length > 0) {
        const result = await uploadMultipleFiles(newCollectionFiles, newCollectionName);
        if (result.err && result.err.length > 0) {
          const errorMessages = result.err.map((e: any) => `${e.fileName}: ${e.error}`).join("\n");
          setAlertConfig({
            open: true,
            type: "error",
            message: `Colección creada pero error al subir algunos archivos:\n${errorMessages}`,
          });
        } else {
          setAlertConfig({
            open: true,
            type: "success",
            message: textConfig?.collectionCreatedSuccessfully || "Colección creada exitosamente",
          });
        }
      } else {
        setAlertConfig({
          open: true,
          type: "success",
          message: textConfig?.collectionCreatedSuccessfully || "Colección creada exitosamente",
        });
      }

      setNewCollectionName("");
      setNewCollectionFiles([]);
      setCollectionNameError("");
      await fetchUserDocuments();
    } catch (error: any) {
      setAlertConfig({
        open: true,
        type: "error",
        message: textConfig?.errorCreatingCollection || `Error al crear colección: ${error.message}`,
      });
    } finally {
      setIsCreatingCollection(false);
    }
  };

  const handleCreateFolder = async (folderName: string, setAlertConfig: React.Dispatch<React.SetStateAction<AlertConfig>>) => {
    if (!folderName.trim()) {
      setAlertConfig({
        open: true,
        type: "error",
        message: textConfig?.folderNameRequired || "El nombre de la carpeta es requerido.",
      });
      return;
    }

    if (folderName.length > 50) {
      setAlertConfig({
        open: true,
        type: "error",
        message: textConfig?.folderNameTooLong || "El nombre de la carpeta no puede exceder los 50 caracteres.",
      });
      return;
    }

    setAlertConfig({
      open: true,
      type: "loading",
      message: textConfig?.creatingFolder || "Creando carpeta...",
    });

    try {
      const folderPath = currentPath.length > 0
        ? `${currentPath.join('/')}/${folderName}`
        : folderName;

      await createEmptyFolderFile(folderPath);

      setAlertConfig({
        open: true,
        type: "success",
        message: textConfig?.folderCreatedSuccessfully || "Carpeta creada exitosamente",
      });

      await fetchUserDocuments();

    } catch (error: any) {
      setAlertConfig({
        open: true,
        type: "error",
        message: textConfig?.errorCreatingFolder || `Error al crear carpeta: ${error.message}`,
      });
    }
  };

  return {
    selectedFiles,
    setSelectedFiles,
    newCollectionFiles,
    setNewCollectionFiles,
    newCollectionName,
    setNewCollectionName,
    isUploading,
    isCreatingCollection,
    fileNameError,
    setFileNameError,
    collectionNameError,
    setCollectionNameError,
    showInlineSuccessMessage,
    setShowInlineSuccessMessage,
    showFileUploadSuccessMessage,
    setShowFileUploadSuccessMessage,
    processFileSelection,
    handleFileSelection,
    handleNewCollectionFileSelection,
    handleUploadFiles,
    handleDeleteFile,
    handleCreateCollection,
    handleCreateFolder,
  };
};